import React, { ReactElement } from 'react';
import { deviceSize } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { useAppStateContext } from 'src/appState/AppState';
import {
    RenderOptimizedVariantsPropsType,
    RenderSourcePropsType,
    SourceSetPropsType,
} from 'src/domains/layouts/webview/components/common/lazyLoadingImage/common/ImagePictureHelpers.type';

const RenderSource = (props: RenderSourcePropsType): ReactElement => {
    const { imgSrc, mediaMaxWidth, format } = props;

    return (
        <source
            srcSet={imgSrc}
            type={format === undefined ? undefined : `image/${format}`}
            media={mediaMaxWidth === undefined ? undefined : `(max-width: ${mediaMaxWidth}px)`}
        />
    );
};

const isImageRelative = (src: string): boolean => {
    return src[0] === '/';
};

export const RenderOptimizedVariants = (props: RenderOptimizedVariantsPropsType): ReactElement | null => {
    const { appLayoutsState } = useAppStateContext();

    const { img, mediaMaxWidth } = props;
    const { src, optimizeOptions } = img;

    if (isImageRelative(src)) {
        return null;
    }

    return (
        <>
            {props.formats.map((format, idx) => {
                const optimizedSrc = appLayoutsState.optimizeImage(src, format, optimizeOptions);

                if (optimizedSrc === null) {
                    return null;
                }

                return (
                    <RenderSource
                        imgSrc={optimizedSrc}
                        format={format}
                        mediaMaxWidth={mediaMaxWidth}
                        key={idx}
                    />
                );
            })}
        </>
    );
};

export const DeviceSourceSet = (props: SourceSetPropsType): ReactElement => {
    const { imgDeviceSet } = props;

    return (
        <>
            {imgDeviceSet?.mobile === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.mobile.src,
                            optimizeOptions: { width: deviceSize.mobile, quality: imgDeviceSet.mobile.quality },
                        }}
                        mediaMaxWidth={deviceSize.mobile}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.mobile.src}
                        mediaMaxWidth={deviceSize.mobile}
                    />
                </>
            )}
            {imgDeviceSet?.tablet === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.tablet.src,
                            optimizeOptions: { width: deviceSize.tablet, quality: imgDeviceSet.tablet.quality },
                        }}
                        mediaMaxWidth={deviceSize.tablet}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.tablet.src}
                        mediaMaxWidth={deviceSize.tablet}
                    />
                </>
            )}
            {imgDeviceSet?.desktop === undefined ? null : (
                <>
                    <RenderOptimizedVariants
                        img={{
                            src: imgDeviceSet.desktop.src,
                            optimizeOptions: { width: deviceSize.desktop, quality: imgDeviceSet.desktop.quality },
                        }}
                        mediaMaxWidth={deviceSize.desktop}
                        formats={['avif', 'webp']}
                    />
                    <RenderSource
                        imgSrc={imgDeviceSet.desktop.src}
                        mediaMaxWidth={deviceSize.desktop}
                    />
                </>
            )}
        </>
    );
};
